/**
 * The console's dictation flow: recorded audio → the server's two-stage
 * pipeline → the composer's pre-fill. The same three calls and poll as the
 * phone (`apps/mobile/src/features/capture/dictation.ts`), against the same
 * routes — `architecture/0019` put the pipeline on the server so that any
 * client which can record a file can dictate.
 *
 * What differs from the phone is where the recording lives while things go
 * wrong. The phone keeps it in a file; the browser keeps it in memory for as
 * long as the composer is open, so the sentences say "in this window" and the
 * dialog offers a download. Nothing the user said is discarded silently
 * (`0019` §7).
 *
 * Pure logic and API calls only — no React, no recorder.
 */
import {
  UPLOAD_REFUSALS,
  isDictationSettled,
  type DictationClarification,
  type NoteExtractionSuggestion,
} from "@gb/contracts";
import { api, errorMessage } from "@/api/client";
import { putWithProgress } from "@/lib/upload";
import { UPLOAD_DIDNT_GO_THROUGH } from "@/lib/upload-failure";

/** What the composer receives when the pipeline settles. */
export type DictationResult = {
  dictationId: string;
  transcript: string;
  suggestion: NoteExtractionSuggestion | null;
  clarifications: DictationClarification[];
};

export class DictationError extends Error {
  constructor(
    message: string,
    /** True when trying again might work — network-shaped failures. */
    readonly retryable: boolean,
  ) {
    super(message);
    this.name = "DictationError";
  }
}

// The phone's sentences, with the phone taken out of them. The three that
// end the flow are word-for-word the phone's, so one fault reads the same
// on both.
export const KEPT_CONNECTION =
  "The connection dropped — the recording is kept in this window. Try again, or type the note.";
export const KEPT_OFFLINE =
  "You’re offline — the recording is kept in this window. Try again when you’re back online, or type the note.";
export const STILL_TRANSCRIBING =
  "Still transcribing — your recording is stored. Try again in a moment, or type the note.";
export const TRANSCRIPTION_OFF =
  "Transcription isn’t switched on for this site yet — type the note instead.";
export const DIDNT_CATCH = "GroundBrain didn’t catch that recording — type the note instead.";
export const LOST_TRACK = "GroundBrain lost track of this recording — type the note instead.";
export const KEPT_UPLOAD_REFUSED =
  "The upload didn’t go through — the recording is kept in this window. Try again, or type the note.";
const NOT_TAKEN = "GroundBrain couldn’t take this recording.";

function transportError(): DictationError {
  return new DictationError(navigator.onLine ? KEPT_CONNECTION : KEPT_OFFLINE, true);
}

/**
 * Reserve, upload, complete. Returns the dictation id; the pipeline runs out
 * of band and `pollDictation` collects it.
 *
 * `mimeType` is what the recorder produced, codecs and all — the server
 * normalises it and echoes the exact type the PUT must carry, because that is
 * the type signed into the URL.
 */
export async function uploadDictation(input: {
  projectId: string;
  date: string;
  audio: Blob;
  mimeType: string;
  extension: string;
  durationSeconds: number;
}): Promise<string> {
  // Refused here, in the API's own words, before anything is reserved.
  if (!(input.audio.size > 0)) throw new DictationError(UPLOAD_REFUSALS.empty, false);

  const id = crypto.randomUUID();
  try {
    const reserved = await api.journals.dictationUploadUrl({
      params: { id: input.projectId },
      body: {
        id,
        date: input.date,
        purpose: "note",
        filename: `dictation.${input.extension}`,
        mimeType: input.mimeType,
        sizeBytes: input.audio.size,
        // The truth, not a clamp: the server owns the cap and refuses over it
        // in its own words (`architecture/0031`).
        durationSeconds: Math.round(input.durationSeconds),
      },
    });
    if (reserved.status !== 201 && reserved.status !== 200) {
      throw new DictationError(reserveRefusal(reserved), reserved.status >= 500);
    }

    const { uploadUrl, contentType } = reserved.body;
    await putWithProgress(
      uploadUrl,
      new File([input.audio], `dictation.${input.extension}`, { type: contentType }),
      () => undefined,
    ).catch((error: unknown) => {
      // Storage answered, but not with success — most often a link that
      // expired. A retry reserves afresh and gets a new one.
      if (error instanceof Error && error.message === UPLOAD_DIDNT_GO_THROUGH) {
        throw new DictationError(KEPT_UPLOAD_REFUSED, true);
      }
      throw transportError();
    });

    const completed = await api.journals.completeDictation({
      params: { id: input.projectId, dictationId: id },
      body: {},
    });
    if (completed.status !== 200) {
      throw new DictationError(reserveRefusal(completed), completed.status >= 500);
    }
    return id;
  } catch (error) {
    if (error instanceof DictationError) throw error;
    // Anything else is a fetch that never got an answer.
    throw transportError();
  }
}

/**
 * Wait for the pipeline. Resolves when every pass has settled; a
 * transcription that failed rejects with the honest sentence, and a timeout
 * says the recording is stored — because it is, and polling again can pick
 * it up.
 *
 * `cancelled` is asked between polls, so a composer closed mid-wait stops
 * asking. It resolves to `null` then, and the caller drops it.
 */
export async function pollDictation(
  projectId: string,
  dictationId: string,
  options: { cancelled?: () => boolean; timeoutMs?: number; intervalMs?: number } = {},
): Promise<DictationResult | null> {
  const timeoutMs = options.timeoutMs ?? POLL_TIMEOUT_MS;
  const intervalMs = options.intervalMs ?? POLL_INTERVAL_MS;
  const cancelled = options.cancelled ?? (() => false);
  const startedAt = Date.now();

  for (;;) {
    if (cancelled()) return null;
    // Only a 404 — the id genuinely unknown — ends a poll early. A blip just
    // spends some of the timeout; the bytes are stored and the pipeline runs.
    let dictation = null;
    try {
      const res = await api.journals.getDictation({ params: { id: projectId, dictationId } });
      if (res.status === 404) throw new DictationError(LOST_TRACK, false);
      if (res.status === 200) dictation = res.body.dictation;
    } catch (error) {
      if (error instanceof DictationError) throw error;
      if (!navigator.onLine) throw new DictationError(KEPT_OFFLINE, true);
    }

    if (dictation && isDictationSettled(dictation)) {
      if (dictation.transcriptionStatus !== "done") {
        throw new DictationError(
          dictation.transcriptionStatus === "skipped" ? TRANSCRIPTION_OFF : DIDNT_CATCH,
          false,
        );
      }
      return {
        dictationId,
        transcript: dictation.transcript ?? "",
        suggestion: dictation.suggestion,
        clarifications: dictation.clarifications,
      };
    }
    if (Date.now() - startedAt > timeoutMs) {
      throw new DictationError(STILL_TRANSCRIBING, true);
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
}

/** The server's own sentence where it gave one — the trim message for a
 *  recording over the cap is the one that matters (`0019` §7). */
function reserveRefusal(res: { status: number; body: unknown }): string {
  const message = errorMessage(res);
  if (message && !message.startsWith("Request failed")) return message;
  return NOT_TAKEN;
}

/** Answer one clarifying question. The answer is recorded in its own right —
 *  it is the improvement corpus (`architecture/0025`). */
export async function answerClarification(input: {
  projectId: string;
  dictationId: string;
  clarificationId: string;
  answer: { chosenIndex: number } | { typedText: string };
}): Promise<DictationClarification[] | null> {
  const res = await api.journals.answerClarification({
    params: {
      id: input.projectId,
      dictationId: input.dictationId,
      clarificationId: input.clarificationId,
    },
    body:
      "chosenIndex" in input.answer
        ? { chosenIndex: input.answer.chosenIndex, typedText: null }
        : { chosenIndex: null, typedText: input.answer.typedText },
  });
  return res.status === 200 ? res.body.dictation.clarifications : null;
}

/** The poll cadence: the pipeline is seconds, the ceiling is patience. */
const POLL_INTERVAL_MS = 1_500;
const POLL_TIMEOUT_MS = 90_000;
